import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Phone, Mail, ExternalLink } from "lucide-react";
import PageMeta from "./PageMeta";
import MembershipApplication from "./MembershipApplication";

export default function ContactPage() {
  const navigate = useNavigate();

  const [contactName, setContactName]       = useState("");
  const [contactEmail, setContactEmail]     = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSent, setContactSent]       = useState(false);
  const [contactError, setContactError]     = useState(false);

  const encode = (data) =>
    Object.keys(data).map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k])).join("&");

  const handleSendEnquiry = (e) => {
    e.preventDefault();
    setContactError(false);
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "contact", name: contactName, email: contactEmail, message: contactMessage }),
    })
      .then(() => {
        setContactSent(true);
        setContactName(""); setContactEmail(""); setContactMessage("");
      })
      .catch(() => setContactError(true));
  };

  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta
        title="Contact & Membership | Redruth Sea Angling Association"
        description="Get in touch with Redruth Sea Angling Association, Cornwall's sea angling club since 1962 — membership enquiries, meeting details and how to join."
        keywords="contact Redruth Sea Angling Association, join sea angling club Cornwall, RSAA membership enquiries"
        url="https://www.redruthseaanglingassociation.co.uk/contact"
      />
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-4">
          <button type="button" onClick={() => navigate(-1)}
            className="standalone-only group inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-black/20 backdrop-blur transition hover:-translate-x-0.5 hover:border-cyan-300/40 hover:bg-cyan-400/10 hover:text-white">
            ← Back
          </button>
          <div>
            <h1 className="heading-glow text-2xl font-bold text-white sm:text-3xl">Contact & Membership</h1>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          {/* Contact info */}
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Contact</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">Get in touch</h2>
            <p className="mt-5 text-base leading-7 text-slate-300">
              Get in touch with us using the form, by phone, or come along to one of our monthly meetings.
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-4 rounded-[1.6rem] border border-white/10 bg-white/5 p-5 panel-lift">
                <MapPin className="mt-1 h-5 w-5 text-cyan-300" />
                <div>
                  <p className="font-semibold text-white">Meeting venue</p>
                  <p className="mt-1 text-sm leading-7 text-slate-300">St Rumons Social Club, Penryn Street, Redruth TR15 2SP</p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("St Rumons Social Club, Penryn Street, Redruth TR15 2SP")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 transition hover:text-cyan-200"
                  >
                    See on map <ExternalLink className="h-3 w-3" />
                  </a>
                  <p className="mt-2 text-xs text-slate-400">2026 dates (8pm): 2nd Mar · 13th Apr · 11th May · 1st Jun · 6th Jul · 3rd Aug · 7th Sep · 5th Oct · 2nd Nov · 7th Dec · AGM Jan 2027</p>
                  <p className="mt-1 text-xs text-slate-500">Some meetings may be rescheduled — members notified by email and WhatsApp</p>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-[1.6rem] border border-white/10 bg-white/5 p-5 panel-lift">
                <Phone className="mt-1 h-5 w-5 text-cyan-300" />
                <div>
                  <p className="font-semibold text-white">Telephone</p>
                  <p className="text-xs text-slate-400">Honorary Secretary — Tim Clotworthy</p>
                  <p className="mt-1 text-sm leading-7 text-slate-300">07766 826693</p>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-[1.6rem] border border-white/10 bg-white/5 p-5 panel-lift">
                <Mail className="mt-1 h-5 w-5 text-cyan-300" />
                <div>
                  <p className="font-semibold text-white">Email</p>
                  <p className="mt-1 text-sm leading-7 text-slate-300">tim.clotworthy@btinternet.com</p>
                </div>
              </div>
            </div>
          </div>

          {/* Enquiry form */}
          <div className="rounded-[2rem] border border-white/10 bg-white/5 p-7 panel-lift">
            <h3 className="text-2xl font-semibold text-white">Send an enquiry</h3>
            {contactSent ? (
              <div className="mt-6 rounded-2xl border border-cyan-300/30 bg-cyan-400/10 px-5 py-4 panel-lift">
                <p className="text-sm text-cyan-100">Message sent — we'll be in touch soon.</p>
                <button type="button" onClick={() => setContactSent(false)}
                  className="mt-3 text-xs font-medium text-cyan-300 underline hover:text-cyan-200">
                  Send another message
                </button>
              </div>
            ) : (
              <form name="contact" method="POST" data-netlify="true" onSubmit={handleSendEnquiry} className="mt-6 grid gap-4">
                <input type="hidden" name="form-name" value="contact" />
                <input type="text" name="name" placeholder="Your name" required
                  value={contactName} onChange={(e) => setContactName(e.target.value)}
                  className="rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-cyan-300/40" />
                <input type="email" name="email" placeholder="Your email" required
                  value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                  className="rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-cyan-300/40" />
                <textarea name="message" rows={6} placeholder="Your message" required
                  value={contactMessage} onChange={(e) => setContactMessage(e.target.value)}
                  className="rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-cyan-300/40" />
                {contactError && (
                  <p className="rounded-2xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300">
                    Sorry, there was a problem. Please email <a href="mailto:tim.clotworthy@btinternet.com" className="underline hover:text-red-200">tim.clotworthy@btinternet.com</a> directly.
                  </p>
                )}
                <button type="submit" className="w-fit rounded-2xl bg-cyan-400 px-5 py-3 font-semibold text-slate-950 transition hover:scale-[1.02]">
                  Send message
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Membership application */}
        <div id="paypal" className="mt-10">
          <MembershipApplication />
        </div>
      </div>
    </div>
  );
}
